'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { faDownload, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { ExportMenuItem } from './components';
import { PulseButton } from 'src/components';

/**
 * TableExportMenu component for exporting grid data in various formats
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onExport - Callback function when export is triggered
 * @param {boolean} [props.isExporting=false] - Whether export is in progress
 * @param {boolean} [props.disabled=false] - Whether the export button is disabled
 * @param {string} [props.table=''] - Identifier for the table being exported
 */
interface TableExportMenuProps {
  onExport: (type: string) => void;
  isExporting?: boolean;
  disabled?: boolean;
  table?: string;
}

const TableExportMenu = ({
  onExport,
  isExporting = false,
  disabled = false,
  table = '',
}: TableExportMenuProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Handle click outside
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    },
    [isMenuOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleExport = useCallback(
    (type: string) => {
      onExport(type);
      setIsMenuOpen(false);
    },
    [onExport]
  );

  const toggleMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Explicitly close if open, open if closed
      if (isMenuOpen) {
        setIsMenuOpen(false);
      } else {
        setIsMenuOpen(true);
      }
    },
    [isMenuOpen]
  );

  return (
    <div className="relative inline-block text-left">
      {/* Button */}
      <div ref={buttonRef}>
        <PulseButton
          size="sm"
          variant="outline"
          color="neutral"
          data-testid={`${table}-export-menu-icon-button`}
          startDecorator={isExporting ? faSpinner : faDownload}
          onClick={toggleMenu}
          disabled={disabled}
          className="w-full max-w-36 h-9 rounded-full"
        >
          Export
        </PulseButton>
      </div>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <div
          id={`${table}-export-menu`}
          data-testid={`${table}-export-menu`}
          className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
          ref={dropdownRef}
          role="menu"
          aria-label="Export options"
        >
          <div className="py-1" role="none">
            <ExportMenuItem
              type="csv"
              onExport={handleExport}
              id={`${table}-export-menu-item-csv`}
              data-testid={`${table}-export-menu-item-csv`}
            />
            <ExportMenuItem
              type="xlsx"
              onExport={handleExport}
              id={`${table}-export-menu-item-xlsx`}
              data-testid={`${table}-export-menu-item-xlsx`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TableExportMenu;
